import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { CameraQrScannerModal } from '../CameraQrScannerModal';
import ScanNumericKeypad from '../scan/ScanNumericKeypad';
import { RouteTransitionLogo } from '../RouteTransitionLogo';
import InfoPopoverButton from '../InfoPopoverButton';
import { useVenue } from '../../contexts/VenueContext';
import { getAuthHeaders } from '../../utils/venueAppApi';
import StaffVenueHeader from './StaffVenueHeader';
import { useIsDemoEnv } from '../../hooks/useIsDemoEnv';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function StaffScanSurface({ basePath, bottomNav, pageTestId, testIdPrefix, roleLabel }) {
  const navigate = useNavigate();
  const { activeVenue } = useVenue();
  const isDemoEnv = useIsDemoEnv();
  const [amount, setAmount] = useState('');
  const [showScanner, setShowScanner] = useState(false);
  const [qrCode, setQrCode] = useState('');
  const [scanning, setScanning] = useState(false);
  const resolvedVenueId = activeVenue?.live_venue_id || activeVenue?.venue_id || null;
  const amountInputTestId = `${testIdPrefix}-scan-amount`;

  const requireVenue = () => {
    if (!resolvedVenueId) {
      toast.error('No active venue is linked yet — ask the venue owner to finish setup before scanning.');
      return false;
    }
    return true;
  };

  const canScan = amount && parseFloat(amount) > 0;

  const handleScan = () => {
    if (!canScan) {
      toast.error('Please enter the total bill amount first');
      return;
    }
    if (!requireVenue()) return;
    setShowScanner(true);
  };

  const createTransaction = async (guestPassId) => {
    const txnRes = await axios.post(`${API}/transactions`, {
      original_amount: parseFloat(amount),
      guest_pass_id: guestPassId,
      venue_id: resolvedVenueId,
    }, { headers: getAuthHeaders() });
    toast.success('QR scanned! Awaiting guest payment...');
    navigate(`${basePath}/awaiting/${txnRes.data.id}`);
  };

  const handleDemoScan = async () => {
    if (!canScan) {
      toast.error('Please enter the total bill amount first');
      return;
    }
    if (!requireVenue()) return;
    setScanning(true);
    try {
      await createTransaction('pass-001');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Demo scan failed');
    } finally {
      setScanning(false);
    }
  };

  const handleSubmitQR = async (codeValue = qrCode) => {
    const resolvedCode = codeValue.trim();
    if (!resolvedCode) {
      toast.error('Please enter the QR code');
      return false;
    }
    if (!requireVenue()) return false;
    setQrCode(resolvedCode);
    setScanning(true);
    try {
      const lookupRes = await axios.get(`${API}/guest-passes/lookup/${encodeURIComponent(resolvedCode)}`, { headers: getAuthHeaders() });
      await createTransaction(lookupRes.data.id);
      return true;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid QR code or scan failed');
      return false;
    } finally {
      setScanning(false);
    }
  };

  return (
    <div className="app-shell" data-testid={pageTestId} style={{ minHeight: '100dvh', backgroundColor: 'var(--app-bg)', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingBottom: 120 }}>
      <StaffVenueHeader roleLabel={roleLabel} testIdPrefix={testIdPrefix} />

      <div className="scan-logo-halo" style={{ marginTop: 18 }}>
        <RouteTransitionLogo width={96} height={86} dataTestId={`${testIdPrefix}-scan-shared-logo`} />
      </div>

      <div className={`scan-amount-card${canScan ? ' is-filled' : ''}`} style={{ marginTop: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
          <label
            htmlFor={`${testIdPrefix}-scan-amount-input`}
            style={{
              fontFamily: "'Inria Serif', serif", fontSize: 12, fontWeight: 600,
              color: 'var(--text-3)', letterSpacing: 1.6, textTransform: 'uppercase',
            }}
          >
            Bill amount
          </label>
          <InfoPopoverButton
            testId={`${testIdPrefix}-scan-amount-info`}
            size="sm"
            align="center"
            title="Bill amount"
            tip="Enter the customer's total bill before any Allure discount. The app will automatically apply their loyalty discount when they pay."
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 6, marginTop: 14, padding: '0 10px' }}>
          <input
            id={`${testIdPrefix}-scan-amount-input`}
            type="text"
            inputMode="none"
            readOnly
            autoComplete="off"
            value={amount}
            onFocus={(e) => e.target.blur()}
            placeholder="0"
            data-testid={amountInputTestId}
            className="scan-amount-field"
            style={{
              width: 200, fontFamily: "'Inria Serif', serif", fontSize: 64, fontWeight: 700,
              color: canScan ? 'var(--text)' : 'var(--text-3)', textAlign: 'right',
              backgroundColor: 'transparent', border: 'none', outline: 'none', padding: 0,
              letterSpacing: -0.5, caretColor: 'transparent', cursor: 'default',
              transition: 'color 200ms ease',
            }}
          />
          <span style={{ fontFamily: "'Inria Serif', serif", fontSize: 30, fontWeight: 500, color: canScan ? 'var(--text)' : 'var(--text-3)', transition: 'color 200ms ease' }}>€</span>
        </div>

        <div style={{ marginTop: 10, padding: '0 6px' }}>
          <div className="scan-amount-underline" />
        </div>
      </div>

      <div className="scan-cta-caption" style={{ marginTop: 24, marginBottom: 0 }}>
        <p style={{ fontFamily: "'Inria Serif', serif", fontSize: 14, fontWeight: 500, color: 'var(--text-3)', textAlign: 'center', margin: 0, letterSpacing: 0.2 }}>
          Type amount, then tap scan
        </p>
        <InfoPopoverButton
          testId={`${testIdPrefix}-scan-qr-info`}
          size="sm"
          align="end"
          title="Scanning the QR"
          tip="Ask the guest to open the Allure app and show their QR code. Tap the SCAN button on the keypad to open the camera."
        />
      </div>

      <ScanNumericKeypad
        value={amount}
        onChange={setAmount}
        onScan={handleScan}
        canScan={!!canScan}
        testIdPrefix={`${testIdPrefix}-scan`}
      />

      <CameraQrScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        title="Scan Guest QR Code"
        manualValue={qrCode}
        onManualChange={setQrCode}
        onScanCode={handleSubmitQR}
        onDemo={process.env.NODE_ENV !== 'production' && isDemoEnv ? handleDemoScan : undefined}
        scanning={scanning}
        modalTestId={`${testIdPrefix}-scan-camera-modal`}
        manualInputTestId={`${testIdPrefix}-scan-manual-input`}
        confirmButtonTestId={`${testIdPrefix}-scan-confirm-button`}
        demoButtonTestId={`${testIdPrefix}-scan-demo-button`}
      />
      {bottomNav}
    </div>
  );
}
